import React from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { hotjar } from 'react-hotjar';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { StyledEngineProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.min.css';

import App from './App';
import { getConfig } from './AppConfig';
import store from './redux/legacy/stores/GlobalStore';
import ThemeProvider from './components/layout/Theme/ThemeProvider';
import SeoProvider from './components/providers/SeoProvider/SeoProvider';

const persistor = persistStore(store);

const config = getConfig();

if (config?.bugsnagKey && /^[a-f0-9]{32}$/i.test(config.bugsnagKey)) {
  Bugsnag.start({
    apiKey: config.bugsnagKey,
    plugins: [new BugsnagPluginReact()],
    appVersion: process.env.REACT_APP_VERSION || '0.0.0',
    enabledReleaseStages: ['production'],
    onError(event) {
      // ChunkLoadError fires when a deploy has replaced old chunk files; the
      // lazyWithRetry helper auto-reloads the page so these self-heal. Suppress
      // them here so they don't pollute the Bugsnag dashboard.
      if (event.errors?.[0]?.errorClass === 'ChunkLoadError') return false;

      const frames = event.errors?.[0]?.stacktrace ?? [];
      const isThirdPartyWebKit =
        frames.length > 0 &&
        frames.every(
          (f) => !f.file || f.file.startsWith('webkit-masked-url://')
        );
      if (isThirdPartyWebKit) return false;
      return true;
    },
  });
}
const hotjarId = config?.hotjarId || 'bogus_hotjar_id';
const hotjarSnippetVersion =
  config?.hotjarSnippetVersion || 'bogus_hotjar_snippet_version';

if (/^\d+$/.test(String(hotjarId))) {
  hotjar.initialize(hotjarId, hotjarSnippetVersion);
}
const ErrorBoundary = Bugsnag.getPlugin('react')
  ? Bugsnag.getPlugin('react').createErrorBoundary(React)
  : ({ children }) => children;

function Root() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <StyledEngineProvider injectFirst>
              <ThemeProvider>
                <App />
              </ThemeProvider>
            </StyledEngineProvider>
            <SeoProvider />
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}
export default Root;
